// «Instalar Ona Pulse» — la página de reparto, DENTRO de la app y por tanto
// detrás del login: nadie que no haya entrado con su cuenta llega aquí.
//
// Detecta el dispositivo y ofrece lo que corresponde: al Android el APK
// (Android permite instalar fuera de su tienda), al iPhone las instrucciones
// para añadir a la pantalla de inicio — hasta que exista TestFlight.
import React, { useEffect, useState } from 'react';
import { Linking, Platform, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { router, Stack } from 'expo-router';
import { WavesMark } from '@/components/Waves';
import Actualizar, { appInstalada } from '@/components/Actualizar';
import { Card, SectionTitle } from '@/components/ui';
import { api, BASE_URL } from '@/api/client';
import { C, F, R } from '@/constants/theme';

type Estado = { android: boolean; bytes?: number; fecha?: string };

const esAndroid = () =>
  Platform.OS === 'android' ||
  (Platform.OS === 'web' && typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent));

export default function DescargarScreen() {
  const insets = useSafeAreaInsets();
  const [estado, setEstado] = useState<Estado | null>(null);
  const [bajando, setBajando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { api.descargaEstado().then(setEstado); }, []);

  /* El enlace no puede llevar la credencial, así que se pide un vale de un solo
     uso y se navega con él. Dura 5 minutos y queda registrado quién lo pidió. */
  const bajar = async () => {
    setError(null); setBajando(true);
    try {
      const v = await api.descargaVale();
      if (!v?.vale) throw new Error('No se pudo preparar la descarga.');
      const url = `${BASE_URL}/descarga/android?v=${encodeURIComponent(v.vale)}`;
      if (Platform.OS === 'web') window.location.assign(url);
      else await Linking.openURL(url.startsWith('http') ? url : `https://pulse.onasystem.net${url}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo preparar la descarga.');
    } finally {
      setBajando(false);
    }
  };

  const mb = estado?.bytes ? (estado.bytes / 1024 / 1024).toFixed(0) : null;

  return (
    <View style={{ flex: 1, backgroundColor: C.bg, paddingTop: insets.top }}>
      <Stack.Screen options={{ headerShown: false }} />
      <ScrollView contentContainerStyle={{ paddingHorizontal: 20, paddingBottom: 32 }}>
        {/* 🔴 Esta pantalla no tenía salida: se abre desde la ficha de usuario y,
            sin barra de navegación, el único camino de vuelta era cerrar la app.
            `back()` no siempre puede (si se entró por enlace directo no hay
            historia), así que hay respaldo a la portada. */}
        <Pressable
          style={s.volver}
          hitSlop={12}
          onPress={() => { if (router.canGoBack()) router.back(); else router.replace('/(tabs)'); }}
        >
          <Text style={s.volverTxt}>‹  Volver</Text>
        </Pressable>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 8, marginBottom: 4 }}>
          <Text style={s.h1}>Instalar <Text style={{ color: C.aqua }}>Ona Pulse</Text></Text>
          <WavesMark width={26} />
        </View>
        <Text style={s.sub}>Para tenerla como una app de verdad, no como una página.</Text>

        {/* 🔴 Si ya estás DENTRO de la app instalada, ofrecerte el instalador es
            absurdo. Lo que hace falta aquí es ponerla al día. */}
        {appInstalada ? (
          <Card>
            <SectionTitle dot={C.good}>Ya la tienes instalada</SectionTitle>
            <Text style={s.p}>
              Estás usando la app instalada, no el navegador. Se pone al día sola cada vez que la
              abres; si acaban de publicar algo y lo quieres ya, aquí lo fuerzas.
            </Text>
            <Actualizar />
          </Card>
        ) : esAndroid() ? (
          <Card>
            <SectionTitle>Android</SectionTitle>
            <Text style={s.p}>
              Descarga el instalador y ábrelo. Android te pedirá permiso para instalar
              desde esta fuente: es normal, la app no viene de su tienda porque es
              interna del grupo.
            </Text>
            {estado && !estado.android ? (
              <Text style={s.aviso}>Todavía no hay versión de Android publicada.</Text>
            ) : (
              <>
                <Pressable style={[s.btn, bajando && { opacity: 0.6 }]} disabled={bajando} onPress={bajar}>
                  <Text style={s.btnTxt}>{bajando ? 'Preparando…' : 'Descargar el instalador'}</Text>
                </Pressable>
                {mb ? <Text style={s.pie}>{mb} MB{estado?.fecha ? ` · publicada el ${estado.fecha}` : ''}</Text> : null}
              </>
            )}
            {error ? <Text style={s.err}>{error}</Text> : null}
          </Card>
        ) : (
          <Card>
            <SectionTitle>iPhone</SectionTitle>
            <Text style={s.p}>
              Apple no permite instalar apps fuera de su tienda, así que de momento
              se añade a la pantalla de inicio — queda con su icono y se abre a
              pantalla completa, igual que una app.
            </Text>
            <View style={{ gap: 8, marginTop: 10 }}>
              {[
                'Abre pulse.onasystem.net en Safari',
                'Toca el botón de compartir, abajo en el centro',
                'Elige «Añadir a pantalla de inicio»',
              ].map((t, i) => (
                <View key={t} style={s.paso}>
                  <View style={s.num}><Text style={s.numTxt}>{i + 1}</Text></View>
                  <Text style={s.pasoTxt}>{t}</Text>
                </View>
              ))}
            </View>
            <Text style={s.pie}>Cuando esté lista la cuenta de Apple, llegará por TestFlight y esto dejará de hacer falta.</Text>
          </Card>
        )}

        <Text style={s.nota}>
          Las mejoras del día a día te llegan solas al abrir la app — solo hace falta
          volver a descargar el instalador cuando cambie algo de fondo, dos o tres
          veces al año.
        </Text>
      </ScrollView>
    </View>
  );
}

const s = StyleSheet.create({
  volver: { alignSelf: 'flex-start', paddingVertical: 10, paddingRight: 16, marginTop: 4 },
  volverTxt: { fontFamily: F.uiSemi, fontSize: 13.5, color: C.aquaSoft },
  h1: { fontFamily: F.displayMed, fontSize: 24, color: C.ink },
  sub: { fontFamily: F.ui, fontSize: 12.5, color: C.muted, marginBottom: 16 },
  p: { fontFamily: F.ui, fontSize: 13, color: C.muted, lineHeight: 20, marginTop: 2 },
  btn: {
    backgroundColor: C.aqua, borderRadius: R.pill, paddingVertical: 15,
    alignItems: 'center', marginTop: 16,
  },
  btnTxt: { fontFamily: F.uiSemi, fontSize: 14.5, color: '#06282E' },
  pie: { fontFamily: F.ui, fontSize: 11, color: C.faint, textAlign: 'center', marginTop: 10, lineHeight: 16 },
  aviso: { fontFamily: F.ui, fontSize: 12.5, color: C.warn, marginTop: 12 },
  err: { fontFamily: F.ui, fontSize: 12, color: C.bad, marginTop: 10 },
  paso: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  num: { width: 22, height: 22, borderRadius: 11, backgroundColor: 'rgba(30,211,199,0.15)', alignItems: 'center', justifyContent: 'center' },
  numTxt: { fontFamily: F.uiBold, fontSize: 11, color: C.aqua },
  pasoTxt: { flex: 1, fontFamily: F.ui, fontSize: 12.5, color: C.ink },
  nota: { fontFamily: F.ui, fontSize: 11, color: C.faint, lineHeight: 17, marginTop: 18, textAlign: 'center' },
});
